import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { _className, Button, ComponentSync, LL_H_C, LL_V_L, ModuleFE_Toaster, TS_Input, TS_Radio } from '@nu-art/thunderstorm-frontend';
import './Component_ProvideInfo_Input.scss';
import { ICONS } from '@app/styles';
import { ModuleFE_Advisor_Expression, ModuleFE_Advisor_Value, ModuleFE_Advisor_Variable } from '@app/advisor-frontend';
import { createRef } from 'react';
import { capitalizeFirstLetter, Debounce, exists, voidFunction } from '@nu-art/ts-common';
import { filterRelevantValues } from '@app/hcs-frontend';
import { ExpressionType_Score, resolveValueDisplay, variableType_AttributeInstance } from '@app/hcs-shared';
import { ModuleFE_ProvideInfo } from '../../../../../modules/provide-info/ModuleFE_ProvideInfo.js';
import { getFlexboxRowCount } from '@nu-art/thunderstorm-frontend/utils/dom-tools/index';
import { AdvisorDropDown_Values } from '../../../AdvisorUiComponents/AdvisorUiComponents.js';
import { DropDown_DefaultCaret } from '@app/styles/frontend/ui/consts';
import { provideInfo_GetActualValue, provideInfo_GetPartialValue, provideInfo_GetSuggestedValue } from './utils.js';
export class Component_ProvideInfo_Input extends ComponentSync {
    __onProvideInfoContextChange = voidFunction;
    __onProvideInfoContextUpdate = (context) => this.forceUpdate();
    //######################### Life Cycle #########################
    deriveStateFromProps(nextProps, state) {
        state.contextData = nextProps.contextData;
        state.initialFocus ??= false;
        return state;
    }
    //######################### Logic #########################
    closeInput = () => {
        ModuleFE_ProvideInfo.context.current.hideInput();
    };
    getInputType = () => {
        const varId = this.state.contextData.input.varId;
        const variable = ModuleFE_Advisor_Variable.cache.unique(varId);
        const findings = this.state.contextData.findings.item;
        if (!varId || !variable)
            return;
        if (variable.type === variableType_AttributeInstance)
            //As of 13.4.2026 attribute instances are only enumerated
            return 'enumerated';
        const finding = findings.toUpdate?.find(ftu => ftu.variableId === varId)
            ?? findings.toCreate?.find(prf => prf.variableId === varId);
        return finding?.type;
    };
    resolveInnerData = () => {
        const varId = this.state.contextData.input.varId;
        if (!varId)
            return;
        const neededVariable = this.state.contextData.neededVariables.find(i => i.variableId === varId);
        if (!neededVariable?.innerVariables?.length)
            return;
        const expression = ModuleFE_Advisor_Expression.cache.find(exp => exp.associatedVar === varId);
        if (!expression)
            return;
        return { expression, neededVariable };
    };
    getVarName = (variable) => {
        if (!variable)
            return;
        if (variable.type !== variableType_AttributeInstance)
            return capitalizeFirstLetter(variable.name);
        const nameParts = variable.name.split('.');
        return `${capitalizeFirstLetter(nameParts[0])} (${nameParts[1]})`;
    };
    onFocus = () => {
        if (this.state.initialFocus)
            return;
        this.setState({ initialFocus: true });
    };
    //######################### Render #########################
    render() {
        const className = _className('provide-info-input', this.state.contextData.input.show && 'show', !this.state.initialFocus && 'attention-grab');
        return _jsxs(LL_V_L, { className: className, onFocus: this.onFocus, tabIndex: 1, children: [this.render_Header(), this.render_Input(), this.render_Inner(), _jsx(Component_ProvideInfo_Controls, { contextData: this.state.contextData }), _jsx(ICONS.advisor_avn_close.component, { onClick: this.closeInput })] });
    }
    render_Header = () => {
        const variable = ModuleFE_Advisor_Variable.cache.unique(this.state.contextData.input.varId);
        const title = this.getVarName(variable) ?? 'No variable selected';
        return _jsx("div", { className: 'provide-info-input__header', children: title });
    };
    render_Input = () => {
        const inputType = this.getInputType();
        switch (inputType) {
            case 'enumerated':
                return _jsx(Component_ProvideInfo_SelectInput, { contextData: this.state.contextData }, this.state.contextData.input.varId);
            case 'numeric':
                return _jsx(Component_ProvideInfo_RawInput, { contextData: this.state.contextData }, this.state.contextData.input.varId);
            default: {
                if (this.state.contextData.input.varId)
                    this.logWarning('No input type!!!', this.state.contextData);
                return _jsx(_Fragment, {});
            }
        }
    };
    render_Inner = () => {
        const innerData = this.resolveInnerData();
        if (!innerData)
            return;
        const disabled = this.state.contextData.calculationInProgress;
        const label = innerData.expression.expressionType === ExpressionType_Score.dbKey ? 'Calculate' : 'Resolve';
        return _jsxs(Button, { disabled: disabled, variant: 'text', onClick: () => {
                ModuleFE_ProvideInfo.expression.open(innerData.expression._id, innerData.neededVariable);
            }, children: [label, " ", _jsx(ICONS.advisor_v5_arrowWithCircle.component, {})] });
    };
}
class Component_ProvideInfo_SelectInput extends ComponentSync {
    bodyRef = createRef();
    radioRef = createRef();
    resizeDebounce = new Debounce(() => this.setInputType(), 50, 500);
    //######################### Life Cycle #########################
    __onWindowResized = () => this.resizeDebounce.trigger();
    deriveStateFromProps(nextProps, state) {
        state.contextData = nextProps.contextData;
        return state;
    }
    componentDidMount() {
        this.setInputType();
    }
    //######################### Logic #########################
    setInputType = () => {
        const container = this.radioRef.current;
        const body = this.bodyRef.current;
        if (!container || !body)
            return;
        const rowCount = getFlexboxRowCount(container);
        if (rowCount > 2) {
            body.classList.remove('radio');
            body.classList.add('drop-down');
        }
        else {
            body.classList.remove('drop-down');
            body.classList.add('radio');
        }
    };
    getValueIds = () => {
        const variable = ModuleFE_Advisor_Variable.cache.unique(this.state.contextData.input.varId);
        const value = ModuleFE_Advisor_Value.cache.unique(variable?.valueId);
        return filterRelevantValues(value);
    };
    selectValue = (valueId) => {
        const varId = this.state.contextData.input.varId;
        if (!varId)
            return;
        ModuleFE_ProvideInfo.context.current.updateValue(varId, valueId);
        this.forceUpdate();
    };
    //######################### Render #########################
    render() {
        const valueIds = this.getValueIds();
        const selectedValueId = provideInfo_GetActualValue(this.state.contextData, this.state.contextData.input.varId);
        const suggestedValueId = exists(selectedValueId)
            ? undefined
            : provideInfo_GetSuggestedValue(this.state.contextData, this.state.contextData.input.varId);
        return _jsxs("div", { className: 'provide-info-input__input', ref: this.bodyRef, children: [this.render_Radio(valueIds, selectedValueId, suggestedValueId), this.render_DropDown(valueIds, selectedValueId)] });
    }
    render_Radio = (valueIds, selectedValueId, suggestedValueId) => {
        return _jsx(TS_Radio, { canUnselect: true, innerRef: this.radioRef, groupName: 'provide-info-values', values: valueIds, checked: selectedValueId, onCheck: value => this.selectValue(value), labelTooltipResolver: valueId => {
                const value = ModuleFE_Advisor_Value.cache.unique(valueId);
                return value ? resolveValueDisplay(value, true) : 'N/A';
            }, labelRenderer: valueId => {
                const value = ModuleFE_Advisor_Value.cache.unique(valueId);
                const label = value ? resolveValueDisplay(value, true) : 'N/A';
                if (!suggestedValueId || valueId !== suggestedValueId)
                    return label;
                return _jsxs(_Fragment, { children: [_jsx(ICONS.advisor_v5_suggestion.component, {}), " ", label] });
            } });
    };
    render_DropDown = (valueIds, selectedValueId) => {
        return _jsx(AdvisorDropDown_Values.selectable, { onSelected: value => this.selectValue(value?._id), selected: selectedValueId, queryFilter: value => valueIds.includes(value._id), canUnselect: true, unselectLabel: 'Unknown', sortBy: [(item) => resolveValueDisplay(item).toLowerCase().trim()], placeholder: 'Unknown', caret: DropDown_DefaultCaret, boundingParentSelector: '.provide-info-input' });
    };
}
class Component_ProvideInfo_RawInput extends ComponentSync {
    //######################### Life Cycle #########################
    deriveStateFromProps(nextProps, state) {
        state.contextData = nextProps.contextData;
        return state;
    }
    shouldReDeriveState() {
        return true;
    }
    shouldComponentUpdate() {
        return true;
    }
    //######################### Logic #########################
    onValue = (val) => {
        const varId = this.state.contextData.input.varId;
        if (!varId)
            return;
        let value;
        if (typeof val === 'string') {
            value = Number(val);
            if (isNaN(value))
                return ModuleFE_Toaster.toastError('Invalid numeric value');
        }
        ModuleFE_ProvideInfo.context.current.updateValue(varId, value);
    };
    getValue = () => {
        const value = provideInfo_GetActualValue(this.state.contextData, this.state.contextData.input.varId);
        const suggestedValue = exists(value)
            ? undefined
            : provideInfo_GetSuggestedValue(this.state.contextData, this.state.contextData.input.varId);
        const partialValue = exists(value) ? undefined : provideInfo_GetPartialValue(this.state.contextData);
        if (suggestedValue)
            return { value: String(suggestedValue), status: 'suggested' };
        if (partialValue)
            return { value: `≥ ${String(partialValue)}`, status: 'partial' };
        const resolved = exists(value) ? String(value) : undefined;
        return { value: resolved, status: 'actual' };
    };
    //######################### Render #########################
    render() {
        const value = this.getValue();
        return _jsx("div", { className: 'provide-info-input__input', children: _jsx(TS_Input, { type: 'number', className: value.status, onBlur: this.onValue, value: value.status !== 'partial' ? value.value : undefined, placeholder: value.status === 'partial' ? value.value : undefined }) });
    }
}
class Component_ProvideInfo_Controls extends ComponentSync {
    //######################### Life Cycle #########################
    __onProvideInfoContextChange = voidFunction;
    __onProvideInfoContextUpdate = (context) => this.forceUpdate();
    deriveStateFromProps(nextProps, state) {
        state.contextData = nextProps.contextData;
        return state;
    }
    //######################### Logic #########################
    getData = () => {
        const scope = this.state.contextData.input.scope;
        if (!scope?.length)
            return { count: 0 };
        if (!this.state.contextData.input.varId)
            return { count: scope.length };
        const currentIndex = scope.indexOf(this.state.contextData.input.varId);
        if (currentIndex === -1)
            return { count: scope.length, nextId: scope[0] };
        return { count: scope.length, nextId: scope[currentIndex + 1], prevId: scope[currentIndex - 1] };
    };
    setVar = (varId) => {
        if (!varId)
            return;
        ModuleFE_ProvideInfo.context.current.selectVariable(varId);
    };
    onDone = () => {
        //Hide input
        ModuleFE_ProvideInfo.context.current.hideInput();
        //If this is not the top level, go back one level
        if (!ModuleFE_ProvideInfo.context.current.isBaseContext())
            this.state.contextData.findings.save();
    };
    //######################### Render #########################
    render() {
        const { count, prevId, nextId } = this.getData();
        const disabled = this.state.contextData.calculationInProgress;
        return _jsxs(LL_H_C, { className: 'provide-info-input__footer', children: [_jsxs(Button, { variant: 'text', disabled: !prevId || disabled, id: 'input-nav-button-back', onClick: () => this.setVar(prevId), children: [_jsx(ICONS.advisor_v5_arrowWithCircle.component, {}), " Back"] }), _jsxs("div", { id: 'input-nav-count', children: [count, " missing"] }), this.render_Next(disabled, nextId)] });
    }
    render_Next = (disabled, nextId) => {
        if (!nextId)
            return _jsx(Button, { disabled: disabled, variant: 'text', onClick: this.onDone, children: "Done" });
        return _jsxs(Button, { variant: 'text', id: 'input-nav-button-next', disabled: disabled, onClick: () => this.setVar(nextId), children: ["Next ", _jsx(ICONS.advisor_v5_arrowWithCircle.component, {})] });
    };
}
//# sourceMappingURL=Component_ProvideInfo_Input.js.map